/**
 * The tables socialErrorProcessor matches raw publish errors against: the API's own customer
 * copy first, then provider errors.
 */

/**
 * Copy the Followr API already writes for the customer (UserFacingPublishException, PublishPost,
 * MetaContainerErrors and the network services). Checked before ERROR_PATTERNS and on the
 * text as stored: a blocked thread part quotes the reason the earlier part failed, which can
 * hold a provider JSON body or text that a pattern below would match. An entry without a
 * message is shown as it is, the first sentence as the message and the rest as the action.
 */
export const USER_READY_PATTERNS = [
  {
    // Keep first: the quoted reason can match any entry after it.
    pattern: /Not published because part (\d+) of this/i,
    message: 'This part was not published because part {part} of the {network} thread failed.',
    action: 'Fix the part marked with the error and publish the thread again.',
    severity: 'warning'
  },
  // A timeout or a missing post id: the post may be live, so "try again" would publish it twice.
  { pattern: /because the post may already be live/i, severity: 'warning' },
  { pattern: /because of an internal error\. Our team has been notified/i, severity: 'warning' },
  // Meta media containers (MetaContainerErrors).
  { pattern: /could not download the media file|timed out while downloading the media/i, severity: 'warning' },
  { pattern: /does not support this video format|does not accept this video's length or aspect ratio/i, severity: 'warning' },
  { pattern: /could not process the media|discarded the uploaded media|took too long to process the media/i, severity: 'warning' },
  // The network services (Facebook, Pinterest, YouTube, TikTok) and PublishPost's fallback.
  { pattern: /could not publish this combination of post type and media/i, severity: 'warning' },
  { pattern: /video pin is missing a cover image/i, severity: 'warning' },
  { pattern: /posts need a video/i, severity: 'warning' },
  { pattern: /could not refresh your connection/i, severity: 'warning' },
  { pattern: /We could not read the (video|media file) attached to this post/i, severity: 'warning' },
  { pattern: /could not publish this post\. Please try again later/i, severity: 'warning' },
  { pattern: /characters and Threads allows up to 500/i, severity: 'warning' },
  { pattern: /The media attached to (this post|part \d+ of this .+? thread) is no longer available/i, severity: 'warning' },
  { pattern: /requires at least one image or video/i, severity: 'warning' },
  { pattern: /connection has expired\. Please reconnect/i, severity: 'warning' },
  { pattern: /returned an error \(HTTP/i, severity: 'warning' },
  { pattern: /Publishing took too long and was stopped/i, severity: 'warning' },
  { pattern: /Publishing could not start in time/i, severity: 'warning' },
  { pattern: /We could not download the media for this post/i, severity: 'warning' },
  { pattern: /Bluesky only accepts videos from accounts with a confirmed email/i, severity: 'warning' },
  // TikTok (TiktokPublishErrors, photo posts and TiktokPhotoImages).
  { pattern: /is limiting requests for this account right now/i, severity: 'warning' },
  { pattern: /has not given .+ permission to post on this account/i, severity: 'warning' },
  { pattern: /does not allow this account to post right now/i, severity: 'warning' },
  { pattern: /photo posts allow up to \d+ characters/i, severity: 'warning' },
  { pattern: /could not prepare the images of this post/i, severity: 'warning' },
  { pattern: /Image \d+ of this post is too large to prepare/i, severity: 'warning' },
  { pattern: /daily (posting|publishing) limit for .+ has been reached/i, severity: 'warning' },
  { pattern: /privacy setting chosen for this post is not available/i, severity: 'warning' },
  // Facebook video uploads, and Meta media still processing when the publish was retried.
  { pattern: /so nothing was published/i, severity: 'warning' },
  { pattern: /is still processing the media for this post/i, severity: 'warning' }
];

/**
 * Error patterns and their corresponding user-friendly messages
 * Each pattern includes:
 * - pattern: regex or string to match against raw error
 * - message: user-friendly message
 * - action: actionable instruction for the user
 * - severity: error severity level (error, warning, info)
 */
export const ERROR_PATTERNS = {
  // Connection/Authentication Errors
  connection: [
    {
      // Followr API copy: a publish on a dead connection (shown as written through
      // USER_READY_PATTERNS) and the 409 of the inbox endpoints. Here for isAuthenticationError.
      pattern: /connection (?:to this social network )?has expired\. Please reconnect/i,
      message: 'Your {network} connection has expired.',
      action: 'Please reconnect {network} in Integrations, then try again.',
      severity: 'warning'
    },
    {
      pattern: /You need to connect your .* account in order to publish your post/i,
      message: 'Your {network} account is not connected.',
      action: 'Please connect your {network} account in the integrations section to publish posts.',
      severity: 'warning'
    },
    {
      pattern: /The provided authorization grant or refresh token is invalid, expired or revoked/i,
      message: 'Your {network} connection has expired.',
      action: 'Please reconnect your {network} account to continue publishing.',
      severity: 'warning'
    },
    {
      pattern: /Error validating access token.*session.*invalidated/i,
      message: 'Your {network} session has expired.',
      action: 'Please reconnect your {network} account. This usually happens when you change your password.',
      severity: 'warning'
    },
    {
      pattern: /Value passed for the token was invalid/i,
      message: 'Your {network} authentication token is invalid.',
      action: 'Please reconnect your {network} account to resolve this issue.',
      severity: 'warning'
    },
    {
      pattern: /Invalid authorization header/i,
      message: 'There\'s an authentication issue with your {network} account.',
      action: 'Please reconnect your {network} account to fix this problem.',
      severity: 'warning'
    },
    {
      pattern: /Access token revoked/i,
      message: 'Your {network} access has been revoked.',
      action: 'Please reconnect your {network} account to continue publishing.',
      severity: 'warning'
    },
    {
      pattern: /Refresh token is expired/i,
      message: 'Your {network} connection has expired.',
      action: 'Please reconnect your {network} account to refresh your access.',
      severity: 'warning'
    }
  ],

  // Account Status/Permission Errors
  accountStatus: [
    {
      pattern: /Your account is temporarily locked/i,
      message: 'Your {network} account is temporarily locked.',
      action: 'Please log in to {network} directly to unlock your account, then try again.',
      severity: 'warning'
    },
    {
      pattern: /The user used for authentication is suspended/i,
      message: 'Your {network} account has been suspended.',
      action: 'Please resolve the suspension with {network} support before publishing.',
      severity: 'warning'
    },
    {
      pattern: /User access is restricted/i,
      message: 'Your {network} account access is restricted.',
      action: 'Please check your {network} account status and resolve any restrictions.',
      severity: 'warning'
    },
    {
      pattern: /You are not permitted to perform this action/i,
      message: 'You don\'t have permission to publish on {network}.',
      action: 'Please check your {network} account permissions or contact support.',
      severity: 'warning'
    },
    {
      pattern: /Application does not have permission for this action/i,
      message: 'The app doesn\'t have permission to publish to your {network} account.',
      action: 'Please reconnect your {network} account and grant all necessary permissions.',
      severity: 'warning'
    },
    {
      pattern: /For security reasons, your account has limited access/i,
      message: 'Your {network} account has limited access for security reasons.',
      action: 'Please log in to {network} directly and follow their security instructions.',
      severity: 'warning'
    }
  ],

  // Content/Format Errors
  contentFormat: [
    {
      pattern: /post contains a link and could not be published/i,
      message: 'Your {network} post contains a link.',
      action: 'Please remove any URLs from your post text and try again.',
      severity: 'warning'
    },
    {
      pattern: /Your Tweet text is too long/i,
      message: 'Your tweet is too long.',
      action: 'Please shorten your tweet text to fit within Twitter\'s character limit.',
      severity: 'warning'
    },
    {
      pattern: /The caption was too long/i,
      message: 'Your {network} caption is too long.',
      action: 'Please shorten your caption to meet {network}\'s character limit.',
      severity: 'warning'
    },
    {
      pattern: /text must be at most \d+ characters long/i,
      message: 'Your {network} text exceeds the character limit.',
      action: 'Please shorten your text to meet {network}\'s requirements.',
      severity: 'warning'
    },
    {
      pattern: /The post is empty/i,
      message: 'Your {network} post is empty.',
      action: 'Please add content or media to your post before publishing.',
      severity: 'warning'
    },
    {
      pattern: /Please include either text or media in your Tweet/i,
      message: 'Your tweet needs content.',
      action: 'Please add text or media to your tweet before publishing.',
      severity: 'warning'
    },
    {
      pattern: /duplicate content/i,
      message: 'This content has already been posted to {network}.',
      action: 'Please modify your content to make it unique before posting.',
      severity: 'warning'
    }
  ],

  // Media/Upload Errors
  mediaUpload: [
    {
      pattern: /Media upload.*failed/i,
      message: 'Failed to upload media to {network}.',
      action: 'Please try uploading your media again or use a different file format.',
      severity: 'warning'
    },
    {
      pattern: /The video file.*format.*don\'t support/i,
      message: 'The video format is not supported by {network}.',
      action: 'Please convert your video to a supported format and try again.',
      severity: 'warning'
    },
    {
      pattern: /The aspect ratio is not supported/i,
      message: 'The image aspect ratio is not supported by {network}.',
      action: 'Please adjust your image dimensions to meet {network}\'s requirements.',
      severity: 'warning'
    },
    {
      pattern: /Only photo or video can be accepted as media type/i,
      message: 'Invalid media type for {network}.',
      action: 'Please use only photos or videos for your {network} post.',
      severity: 'warning'
    },
    {
      pattern: /Images must have the same.*ratio/i,
      message: 'All images must have the same aspect ratio for {network}.',
      action: 'Please ensure all images have matching dimensions.',
      severity: 'warning'
    },
    {
      pattern: /Media upload took too long/i,
      message: 'Media upload to {network} timed out.',
      action: 'Please try uploading again or use a smaller file size.',
      severity: 'warning'
    },
    {
      pattern: /No video asset found/i,
      message: 'Video file not found for {network} upload.',
      action: 'Please ensure your video file is properly attached and try again.',
      severity: 'warning'
    }
  ],

  // Rate Limiting/Spam Errors
  rateLimiting: [
    {
      pattern: /Too Many Requests/i,
      message: 'You\'ve exceeded {network}\'s rate limit.',
      action: 'Please wait a few minutes before trying to publish again.',
      severity: 'warning'
    },
    {
      pattern: /Application request limit reached/i,
      message: 'You\'ve reached the {network} posting limit.',
      action: 'Please wait before posting again or try reducing your posting frequency.',
      severity: 'warning'
    },
    {
      pattern: /User has reached the rate limit for publishing/i,
      message: 'You\'ve reached your {network} publishing limit.',
      action: 'Please wait before publishing more content to {network}.',
      severity: 'warning'
    },
    {
      pattern: /too many posts.*24 hours/i,
      message: 'You\'ve posted too frequently on {network}.',
      action: 'Please wait 24 hours before posting again to avoid spam restrictions.',
      severity: 'warning'
    },
    {
      pattern: /User is performing too many actions/i,
      message: 'You\'re performing actions too quickly on {network}.',
      action: 'Please slow down and wait a few minutes before trying again.',
      severity: 'warning'
    },
    {
      pattern: /blocked.*spam/i,
      message: 'Your content was blocked by {network}\'s spam filter.',
      action: 'Please modify your content and try again, or contact {network} support.',
      severity: 'warning'
    }
  ],

  // Platform-Specific Errors
  platformSpecific: [
    {
      pattern: /Board not found/i,
      message: 'The Pinterest board was not found.',
      action: 'Please select a valid Pinterest board or create a new one.',
      severity: 'warning'
    },
    {
      pattern: /The user is not an Instagram Business/i,
      message: 'Your Instagram account needs to be a business account.',
      action: 'Please convert your Instagram account to a business account.',
      severity: 'warning'
    },
    {
      pattern: /Application not found/i,
      message: 'The {network} application configuration is missing.',
      action: 'Please contact support to resolve this configuration issue.',
      severity: 'warning'
    },
    {
      pattern: /User not found/i,
      message: 'Your {network} user account was not found.',
      action: 'Please reconnect your {network} account.',
      severity: 'warning'
    },
    {
      pattern: /User account is not verified/i,
      message: 'Your {network} account is not verified.',
      action: 'Please verify your {network} account before publishing.',
      severity: 'warning'
    },
    {
      pattern: /unconfirmed_email/i,
      message: 'Your Bluesky account email is not confirmed.',
      action: 'Please go to your Bluesky account settings and confirm your email address. Video uploads require a verified email.',
      severity: 'warning'
    },
    {
      pattern: /Invalid Bluesky handle or app password/i,
      message: 'Your Bluesky credentials are invalid.',
      action: 'Please check your Bluesky handle and app password, then reconnect your account.',
      severity: 'warning'
    },
    {
      pattern: /Video processing failed/i,
      message: 'Your video could not be processed by Bluesky.',
      action: 'Please ensure your video is in a supported format (MP4) and under 50MB, then try again.',
      severity: 'warning'
    },
    {
      pattern: /Video processing timed out/i,
      message: 'Video processing on Bluesky took too long.',
      action: 'Please try again with a shorter or smaller video file.',
      severity: 'warning'
    },
    {
      pattern: /Video upload failed/i,
      message: 'Failed to upload your video to Bluesky.',
      action: 'Please try again. If the problem persists, check that your video is in MP4 format and under 50MB.',
      severity: 'warning'
    },
    {
      pattern: /Could not resolve PDS DID/i,
      message: 'Could not connect to your Bluesky data server.',
      action: 'Please try again later or reconnect your Bluesky account.',
      severity: 'warning'
    },
    {
      pattern: /ExpiredToken/i,
      message: 'Your {network} session has expired.',
      action: 'Please reconnect your {network} account to refresh your access.',
      severity: 'warning'
    },
    {
      pattern: /InvalidToken/i,
      message: 'Your {network} authentication token is invalid.',
      action: 'Please reconnect your {network} account to resolve this issue.',
      severity: 'warning'
    },
    {
      pattern: /AccountTakedown/i,
      message: 'Your {network} account has been suspended.',
      action: 'Please contact {network} support to resolve your account status.',
      severity: 'warning'
    },
    {
      pattern: /BlobNotFound/i,
      message: 'The uploaded media could not be found on {network}.',
      action: 'Please try uploading your media again.',
      severity: 'warning'
    },
    {
      pattern: /XRPCNotSupported/i,
      message: '{network} does not support this operation.',
      action: 'Please contact support if this issue persists.',
      severity: 'warning'
    },
    {
      pattern: /DOCTYPE/i,
      message: 'External error: {network} is experiencing technical difficulties.',
      action: 'Please try again later or contact support if the issue continues.',
      severity: 'warning'
    }
  ],

  // Server/Technical Errors
  serverErrors: [
    {
      pattern: /App\\Jobs\\PublishPost has been attempted too many times/i,
      message: 'Publishing to {network} failed after multiple attempts.',
      action: 'Please try again later or contact support if the problem persists.',
      severity: 'warning'
    },
    {
      pattern: /Service.*unavailable/i,
      message: '{network} service is temporarily unavailable.',
      action: 'Please try again later when {network} service is restored.',
      severity: 'warning'
    },
    {
      pattern: /Internal Server Error/i,
      message: '{network} is experiencing technical difficulties.',
      action: 'Please try again later or contact support if the issue continues.',
      severity: 'warning'
    },
    {
      pattern: /Operation timed out/i,
      message: 'Connection to {network} timed out.',
      action: 'Please try again later when {network} service is restored.',
      severity: 'warning'
    },
    {
      pattern: /An unexpected error has occurred/i,
      message: 'An unexpected error occurred with {network}.',
      action: 'Please try again later or contact support if the problem persists.',
      severity: 'warning'
    },
    {
      pattern: /Something went wrong/i,
      message: 'Something went wrong with {network}.',
      action: 'Please try again later or contact support if the issue continues.',
      severity: 'warning'
    }
  ],

  // Generic/Fallback Errors
  generic: [
    {
      pattern: /Previous post could not be published/i,
      message: 'Your previous {network} post couldn\'t be published.',
      action: 'Please check your {network} connection and try publishing again.',
      severity: 'warning'
    },
    {
      pattern: /Forbidden/i,
      message: 'Access to {network} was denied.',
      action: 'Please reconnect your {network} account or check your permissions.',
      severity: 'warning'
    },
    {
      pattern: /Unauthorized/i,
      message: 'You\'re not authorized to publish to {network}.',
      action: 'Please reconnect your {network} account with proper permissions.',
      severity: 'warning'
    }
  ]
};
